import React, { useMemo, useContext } from "react";
import { BehaviorSubject, Subject } from "rxjs";
import { withLatestFrom } from "rxjs/operators";
import { Observer } from "rxjs/internal/types";
import { makeStyles } from "@material-ui/core/styles";

import { FormProvider } from "./FormContext";
import {
  formRemoveField,
  formSetErrors,
  formUpdateField,
  formRegister,
  formReset,
  isFormValid
} from "./formHelpers";
import {
  FieldActionTypes,
  FormActionTypes,
  IFieldAction,
  IFormAction,
  IFormState,
  IFormValues,
  IFormMeta,
  TErrors
} from "./formInterfaces";
import { ConfirmContext } from "../../components/ConfirmContext";
import { log } from "./utils";

const useStyles = makeStyles(() => ({
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    flexDirection: "column"
  }
}));

interface IRxFormProps {
  children: React.ReactNode;
  onSubmit: (
    values: IFormValues,
    meta: IFormMeta,
    helpers: {
      resetForm: () => void;
      setErrors: (errors: TErrors) => any;
    }
  ) => any;
  className?: string;
  confirmMessage: string;
}

export function RxForm(props: IRxFormProps) {
  const confirmContext = useContext(ConfirmContext);
  const classes = useStyles();

  const formCtxValue = useMemo(() => {
    const formStateSubject$ = new BehaviorSubject<IFormState>({
      fields: {},
      values: {},
      meta: {
        submitting: false,
        changed: false,
        completed: false
      }
    });
    const formActionSubject$ = new Subject<IFieldAction>();

    const notifyFormActionChange = (action: IFieldAction) => {
      formActionSubject$.next(action);
    };

    const dispatch = (action: IFieldAction | IFormAction) => {
      const formState = formStateSubject$.getValue();

      switch (action.type) {
        case FieldActionTypes.register: {
          const nextFormState = formRegister(formState, action as IFieldAction);
          formStateSubject$.next(nextFormState);

          log({
            action,
            preState: formState,
            nextState: nextFormState
          });
          break;
        }
        case FieldActionTypes.change: {
          const nextFormState = formUpdateField(
            formState,
            action as IFieldAction
          );
          formStateSubject$.next(nextFormState);
          notifyFormActionChange(action as IFieldAction);
          log({
            action,
            preState: formState,
            nextState: nextFormState
          });
          break;
        }
        case FieldActionTypes.destroy: {
          const nextFormState = formRemoveField(
            formState,
            action as IFieldAction
          );
          formStateSubject$.next(nextFormState);
          log({
            action,
            preState: formState,
            nextState: nextFormState
          });
          break;
        }
        case FormActionTypes.startSubmit: {
          const nextFormState = {
            values: formState.values,
            fields: formState.fields,
            meta: {
              ...formState.meta,
              submitting: true
            }
          };
          formStateSubject$.next(nextFormState);
          break;
        }
        case FormActionTypes.endSubmit: {
          const nextFormState = {
            values: formState.values,
            fields: formState.fields,
            meta: {
              ...formState.meta,
              submitting: false,
              changed: false
            }
          };
          formStateSubject$.next(nextFormState);
          break;
        }

        case FormActionTypes.reset: {
          const nextFormState = formReset(formState);
          log({
            action,
            preState: formState,
            nextState: nextFormState
          });
          formStateSubject$.next(nextFormState);
          break;
        }
      }
    };

    const resetForm = () => {
      dispatch({
        type: FormActionTypes.reset
      });
    };

    const setErrors = (errors: TErrors) => {
      const fromState = formStateSubject$.getValue();
      const nextFormState = {
        values: fromState.values,
        fields: formSetErrors(errors, fromState.fields),
        meta: fromState.meta
      };

      formStateSubject$.next(nextFormState);
    };
    const getFormMeta = () => {
      return formStateSubject$.getValue().meta;
    };

    const getFormValues = () => {
      return formStateSubject$.getValue().values;
    };

    const getFormFields = () => formStateSubject$.getValue().fields;

    const getFormState = () => {
      return formStateSubject$.getValue();
    };

    return {
      subscribe: (observer: Observer<any>) => {
        return formStateSubject$.subscribe(observer);
      },
      subscribeFormAction: (observer: Observer<any>) => {
        return formActionSubject$
          .pipe(withLatestFrom(formStateSubject$))
          .subscribe(observer);
      },
      dispatch,
      updateFormState: (formState: IFormState) => {
        formStateSubject$.next(formState);
      },
      updateFormValues: (formValues: IFormValues) => {
        const formState = formStateSubject$.getValue();
        formStateSubject$.next({
          ...formState,
          values: formValues
        });
      },
      getFormValues,
      getFormFields,
      getFormState,
      getFormMeta,
      setErrors,
      resetForm
    };
  }, []);

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();

    if (!isFormValid(formCtxValue.getFormFields())) {
      return;
    }

    await confirmContext.confirm(props.confirmMessage);

    formCtxValue.dispatch({
      type: FormActionTypes.startSubmit
    });
    await props.onSubmit(
      formCtxValue.getFormValues(),
      formCtxValue.getFormMeta(),
      {
        resetForm: formCtxValue.resetForm,
        setErrors: formCtxValue.setErrors
      }
    );

    formCtxValue.dispatch({
      type: FormActionTypes.endSubmit
    });
  };

  return (
    <FormProvider value={formCtxValue}>
      <form className={props.className || classes.form} onSubmit={handleSubmit}>
        {props.children}
      </form>
    </FormProvider>
  );
}
